import { CANDIDATE_AE_FOLDER_STATUS, AE_FOLDER_IDENTITY_LIST } from "@core/constants";
import { AEBannerModel } from "@core/models/common/ae-banner-status.model";
import { CandidateAeFolderFileModel } from "@core/models/candidates/profile/candidate-ae-folder-file.model";
import { TimelineOptionModel } from "@core/models/common/extra-timeline.model";

export const ICandidateAEFolder = Object.freeze({
  status: "status",
  bannerData: "message",
  siret: "siret",
  tva: "tva",
  iban: "iban",
  swift: "swift",
  identity: "doc_identite",
  identityVerso: "doc_identite_verso",
  rib: "doc_rib",
  insee: "doc_insee",
  isComplete: "is_complete",
  isEmpty: "is_empty",
  identityDocumentType: "doc_type",
  residence: "doc_residence",
  timeline: "timeline",
  residenceDocumentType: "doc_residence_type",
  permitDocument: "doc_residence_permit",
  permitDocumentVerso: "doc_residence_permit_verso",
});

export class CandidateAEFolderModel {
  constructor(data = {}) {
    this[ICandidateAEFolder.status] = data[ICandidateAEFolder.status];
    this[ICandidateAEFolder.siret] = data[ICandidateAEFolder.siret];
    this[ICandidateAEFolder.tva] = data[ICandidateAEFolder.tva];
    this[ICandidateAEFolder.iban] = data[ICandidateAEFolder.iban];
    this[ICandidateAEFolder.swift] = data[ICandidateAEFolder.swift];
    this[ICandidateAEFolder.identity] = data[ICandidateAEFolder.identity];
    this[ICandidateAEFolder.identityVerso] = data[ICandidateAEFolder.identityVerso];
    this[ICandidateAEFolder.rib] = data[ICandidateAEFolder.rib];
    this[ICandidateAEFolder.insee] = data[ICandidateAEFolder.insee];
    this[ICandidateAEFolder.isComplete] = data[ICandidateAEFolder.isComplete];
    this[ICandidateAEFolder.isEmpty] = data[ICandidateAEFolder.isEmpty];
    this[ICandidateAEFolder.bannerData] = data[ICandidateAEFolder.bannerData];
    this[ICandidateAEFolder.identityDocumentType] = data[ICandidateAEFolder.identityDocumentType];
    this[ICandidateAEFolder.residenceDocumentType] = data[ICandidateAEFolder.residenceDocumentType];
    this[ICandidateAEFolder.permitDocument] = data[ICandidateAEFolder.permitDocument];
    this[ICandidateAEFolder.permitDocumentVerso] = data[ICandidateAEFolder.permitDocumentVerso];
    this[ICandidateAEFolder.residence] = data[ICandidateAEFolder.residence];
    this[ICandidateAEFolder.timeline] = data[ICandidateAEFolder.timeline];
  }

  getIdentity() {
    return new CandidateAeFolderFileModel(this[ICandidateAEFolder.identity]);
  }

  getIdentityVerso() {
    return new CandidateAeFolderFileModel(this[ICandidateAEFolder.identityVerso]);
  }

  getPermit() {
    return new CandidateAeFolderFileModel(this[ICandidateAEFolder.permitDocument]);
  }

  getPermitVerso() {
    return new CandidateAeFolderFileModel(this[ICandidateAEFolder.permitDocumentVerso]);
  }

  getRib() {
    return new CandidateAeFolderFileModel(this[ICandidateAEFolder.rib]);
  }

  getInsee() {
    return new CandidateAeFolderFileModel(this[ICandidateAEFolder.insee]);
  }

  getResidenceDocument() {
    return new CandidateAeFolderFileModel(this[ICandidateAEFolder.residence]);
  }

  getIban() {
    return this[ICandidateAEFolder.iban];
  }

  getTva() {
    return this[ICandidateAEFolder.tva];
  }

  getSiret() {
    return this[ICandidateAEFolder.siret];
  }

  getSwift() {
    return this[ICandidateAEFolder.swift];
  }

  getIdentityDocumentType() {
    return this.getIdentity().getDocumentType() || this.getIdentityVerso().getDocumentType();
  }

  getResidenceDocumentType() {
    return this.getPermit().getDocumentType() || this.getPermitVerso().getDocumentType();
  }

  isAdministrativeInfosFormComplete() {
    return this.getSiret() && this.getIban();
  }

  isPassportSelected() {
    return (
      this.getIdentityDocumentType() === AE_FOLDER_IDENTITY_LIST.PASSEPORT_FR ||
      this.getIdentityDocumentType() === AE_FOLDER_IDENTITY_LIST.PASSEPORT_UE
    );
  }

  isCNIApartFromUESelected() {
    return this.getIdentityDocumentType() === AE_FOLDER_IDENTITY_LIST.CNI_APART_FROM_UE;
  }

  isPassportApartFromUESelected() {
    return this.getIdentityDocumentType() === AE_FOLDER_IDENTITY_LIST.PASSEPORT_APART_FROM_UE;
  }

  docsUploadedAmount() {
    let docsCount = 0;
    if (this.getIdentity().getFileName()) {
      docsCount += 1;
    }
    if (this.getIdentityVerso().getFileName()) {
      docsCount += 1;
    }
    if (this.getInsee().getFileName()) {
      docsCount += 1;
    }
    if (this.getRib().getFileName()) {
      docsCount += 1;
    }
    if (this.getPermit().getFileName()) {
      docsCount += 1;
    }
    if (this.getPermitVerso().getFileName()) {
      docsCount += 1;
    }
    return docsCount;
  }

  isFolderComplete() {
    return this[ICandidateAEFolder.isComplete];
  }

  isFolderEmpty() {
    return this[ICandidateAEFolder.isEmpty];
  }

  getStatus() {
    return this[ICandidateAEFolder.status];
  }

  isPending() {
    return this[ICandidateAEFolder.status] === CANDIDATE_AE_FOLDER_STATUS.STATUS_PENDING;
  }

  isPendingUntilVerify() {
    return this[ICandidateAEFolder.status] === CANDIDATE_AE_FOLDER_STATUS.STATUS_PENDING_INTERNAL_VERIFICATION;
  }

  isVerified() {
    return this[ICandidateAEFolder.status] === CANDIDATE_AE_FOLDER_STATUS.STATUS_VERIFIED;
  }

  getBannerData() {
    return new AEBannerModel(this[ICandidateAEFolder.bannerData]);
  }

  getTimeLine() {
    return (this[ICandidateAEFolder.timeline] || []).map((item) => new TimelineOptionModel(item));
  }
}
